import React, { useEffect, useRef, useState } from "react";
import { useMutation, useQuery } from "@apollo/client";
import { makeStyles } from "@material-ui/core/styles";

import { GraphqlErrorMessage } from "../../../components/ErrorMessage";
import { Loading } from "../../../components/LoadingFork";
import { withInstallation } from "../../../components/InstallationContext";
import { T } from "../constants";
import Panel from "../components/Panel";
import Toast from "../components/Toast";
import VipPageShell, { HeaderCount } from "../vip/components/VipPageShell";
import ContactRow from "./components/ContactRow";
import AddContactForm from "./components/AddContactForm";
import {
  contactsQuery,
  createContactMutation,
  deleteContactMutation,
  updateContactMutation,
} from "./queries";

const useStyles = makeStyles({
  blurb: { maxWidth: 760, marginBottom: 24, fontSize: 14, color: T.mutedFg },
  grid: {
    display: "grid",
    gap: 24,
    gridTemplateColumns: "1fr 360px",
    alignItems: "start",
    "@media (max-width: 1100px)": { gridTemplateColumns: "1fr" },
  },
  panelHead: { borderBottom: `1px solid ${T.borderSoft}`, background: T.surface, padding: "12px 20px" },
  panelTitle: { margin: 0, fontSize: 16, fontWeight: 600, color: T.fg },
  panelSub: { margin: "2px 0 0", fontSize: 12, color: T.mutedFg },
  list: { listStyle: "none", margin: 0, padding: 0 },
  empty: { padding: 20, color: T.mutedFg, fontSize: 14 },
  error: { marginBottom: 24 },
});

function nullableTrim(value) {
  const trimmed = value?.trim();
  return trimmed ? trimmed : null;
}

function contactFields(contact) {
  return {
    name: contact.name.trim(),
    ...(contact.type ? { type: contact.type } : {}),
    description: nullableTrim(contact.description),
    email: nullableTrim(contact.email),
    phone: nullableTrim(contact.phone),
    smsTemplate: contact.smsTemplate ?? null,
  };
}

function VipRouting({ installation, match }) {
  const s = useStyles();
  const installationId = installation?.id;
  const [toast, setToast] = useState(null);
  const [mutationError, setMutationError] = useState(null);
  const timer = useRef(null);

  const { data = {}, loading, error, refetch } = useQuery(contactsQuery, {
    variables: { installationId },
    skip: !installationId,
    fetchPolicy: "no-cache",
  });
  const [createContact, createState] = useMutation(createContactMutation);
  const [updateContact, updateState] = useMutation(updateContactMutation);
  const [deleteContact, deleteState] = useMutation(deleteContactMutation);

  const contacts = data.contacts || [];
  const mutationInFlight = createState.loading || updateState.loading || deleteState.loading;

  useEffect(() => () => clearTimeout(timer.current), []);

  const flash = (message) => {
    setToast(message);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setToast(null), 3000);
  };

  const reloadContacts = () => refetch({ installationId });

  const createVipContact = async (contact) => {
    setMutationError(null);

    try {
      await createContact({
        variables: {
          contact: {
            installationId,
            ...contactFields(contact),
          },
        },
      });
      await reloadContacts();
      flash(`${contact.name.trim()} added to VIP routing.`);
    } catch (err) {
      setMutationError(err);
      return false;
    }

    return true;
  };

  const updateVipContact = async (contact) => {
    setMutationError(null);

    try {
      await updateContact({
        variables: {
          contact: {
            id: contact.id,
            name: contact.name.trim(),
            description: nullableTrim(contact.description),
            email: nullableTrim(contact.email),
            phone: nullableTrim(contact.phone),
            smsTemplate: contact.smsTemplate ?? null,
          },
        },
      });
      await reloadContacts();
      flash(`${contact.name.trim()} updated.`);
    } catch (err) {
      setMutationError(err);
      return false;
    }

    return true;
  };

  const deleteVipContact = async (contact) => {
    setMutationError(null);

    try {
      await deleteContact({ variables: { id: contact.id } });
      await reloadContacts();
      flash(`${contact.name} removed.`);
    } catch (err) {
      setMutationError(err);
      return false;
    }

    return true;
  };

  if (loading) return <Loading />;
  if (error) return <GraphqlErrorMessage error={error} />;

  return (
    <VipPageShell
      eyebrow="Admin - Configuration"
      title="VIP routing"
      right={<HeaderCount label="Contacts" value={contacts.length} />}
    >
      <p className={s.blurb}>
        Configure the contacts used by Concierge VIP routing. These records are scoped to the current installation and
        can be selected by features that need to notify a hotel team by SMS or email.
      </p>

      {mutationError && <GraphqlErrorMessage className={s.error} error={mutationError} />}

      <div className={s.grid}>
        <Panel>
          <div className={s.panelHead}>
            <h2 className={s.panelTitle}>Contacts</h2>
            <p className={s.panelSub}>Edit a row, save it, or remove contacts that are no longer used.</p>
          </div>
          {contacts.length ? (
            <ul className={s.list}>
              {contacts.map((contact) => (
                <ContactRow
                  key={contact.id}
                  contact={contact}
                  disabled={mutationInFlight}
                  designUrl={`${match.url}/${contact.id}`}
                  onDelete={deleteVipContact}
                  onSave={updateVipContact}
                />
              ))}
            </ul>
          ) : (
            <div className={s.empty}>No contacts have been created for this installation.</div>
          )}
        </Panel>

        <Panel>
          <AddContactForm disabled={mutationInFlight || !installationId} onAdd={createVipContact} />
        </Panel>
      </div>

      {toast && <Toast>{toast}</Toast>}
    </VipPageShell>
  );
}

export default withInstallation(VipRouting);
